import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Space, Button, DatePicker, Input } from 'antd';
import { PieChart, Pie, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { studentService } from '../../services/studentService';
import AV from 'leancloud-storage';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Search } = Input;

const StudentStats = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [stats, setStats] = useState({
    // Overall totals (regardless of date)
    totalStudents: 0,
    totalActiveStudents: 0,
    totalWithdrawnStudents: 0,
    totalGraduatedStudents: 0,
    
    // Date range specific stats
    newRegistrations: 0,
    newWithdrawals: 0,
    newGraduates: 0,
    statusDistribution: [],
    monthlyRegistrations: [],
    referrerStats: [],
  });
  const [referrerSearch, setReferrerSearch] = useState('');

  const handleQuickSelect = (period) => {
    switch (period) {
      case 'today':
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        break;
      case 'week':
        setDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
        break;
      case 'year':
        setDateRange([dayjs().startOf('year'), dayjs().endOf('year')]);
        break;
      default:
        // Handle month selection (1-12)
        if (typeof period === 'number' && period >= 1 && period <= 12) {
          const year = dayjs().year();
          const startOfMonth = dayjs(`${year}-${period}-01`);
          setDateRange([
            startOfMonth.startOf('month'),
            startOfMonth.endOf('month')
          ]);
        }
        break;
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [startDate, endDate] = dateRange;
      
      // Get all students with pagination
      const query = new AV.Query('Student');
      query.addDescending('createdAt');
      const limit = 1000;
      let skip = 0;
      let allStudents = [];
      
      // Keep fetching until we get all students
      while (true) {
        query.limit(limit);
        query.skip(skip);
        const batch = await query.find();
        
        if (batch.length === 0) {
          break; // No more students to fetch
        }
        
        allStudents = allStudents.concat(batch);
        skip += limit;
        
        if (batch.length < limit) {
          break; // Last batch
        }
      }

      // Overall totals (regardless of date)
      const totalStudents = allStudents.length;
      const totalActiveStudents = allStudents.filter(s => s.get('status') === '正常').length;
      const totalWithdrawnStudents = allStudents.filter(s => s.get('status') === '退学').length;
      const totalGraduatedStudents = allStudents.filter(s => s.get('status') === '已毕业').length;

      // Stats within date range
      const newRegistrations = allStudents.filter(student => {
        const signUpDate = dayjs(student.get('signUpDate'));
        return signUpDate.isAfter(startDate) && signUpDate.isBefore(endDate);
      }).length;

      const newWithdrawals = allStudents.filter(student => {
        const withdrawDate = dayjs(student.get('withdrawDate'));
        return withdrawDate && withdrawDate.isAfter(startDate) && withdrawDate.isBefore(endDate);
      }).length;

      const newGraduates = allStudents.filter(student => {
        const graduateDate = dayjs(student.get('graduateDate'));
        return graduateDate && graduateDate.isAfter(startDate) && graduateDate.isBefore(endDate);
      }).length;

      // Status distribution within date range
      const statusCount = allStudents.reduce((acc, student) => {
        const status = student.get('status') || '未知';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const statusDistribution = Object.entries(statusCount).map(([name, value]) => ({
        name,
        value
      }));

      // Monthly registrations within date range
      const monthlyData = allStudents
        .filter(student => {
          const signUpDate = dayjs(student.get('signUpDate'));
          return signUpDate.isAfter(startDate) && signUpDate.isBefore(endDate);
        })
        .reduce((acc, student) => {
          const month = dayjs(student.get('signUpDate')).format('YYYY-MM');
          acc[month] = (acc[month] || 0) + 1;
          return acc;
        }, {});

      const monthlyRegistrations = Object.entries(monthlyData)
        .map(([month, count]) => ({
          month,
          count
        }))
        .sort((a, b) => a.month.localeCompare(b.month));

      // Calculate referrer statistics and sort by count in descending order
      const referrerCount = allStudents.reduce((acc, student) => {
        const referrer = student.get('referrer') || '无推荐人';
        acc[referrer] = (acc[referrer] || 0) + 1;
        return acc;
      }, {});

      const referrerStats = Object.entries(referrerCount)
        .map(([name, count]) => ({ 
          key: name,  // Add a unique key for each row
          name, 
          count 
        }))
        .filter(item => item.name !== '无推荐人')
        .sort((a, b) => b.count - a.count); // Sort by count in descending order

      setStats(prev => ({
        ...prev,
        totalStudents,
        totalActiveStudents,
        totalWithdrawnStudents,
        totalGraduatedStudents,
        newRegistrations,
        newWithdrawals,
        newGraduates,
        statusDistribution,
        monthlyRegistrations,
        referrerStats,
      }));

    } catch (error) {
      console.error('Error fetching student stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const renderMonthButtons = () => {
    const months = [
      '一月', '二月', '三月', '四月', '五月', '六月',
      '七月', '八月', '九月', '十月', '十一月', '十二月'
    ];
    
    return (
      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        {months.map((month, index) => (
          <Col key={month}>
            <Button 
              size="small"
              onClick={() => handleQuickSelect(index + 1)}
              type={dateRange[0]?.month() === index ? 'primary' : 'default'}
            >
              {month}
            </Button>
          </Col>
        ))}
      </Row>
    );
  };

  const renderOverallStats = () => (
    <Card title="总体统计" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col span={6}>
          <Statistic
            title="总学员数"
            value={stats.totalStudents}
            loading={loading}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="总在读学员"
            value={stats.totalActiveStudents}
            loading={loading}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="总退学学员"
            value={stats.totalWithdrawnStudents}
            loading={loading}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="总毕业学员"
            value={stats.totalGraduatedStudents}
            loading={loading}
          />
        </Col>
      </Row>
    </Card>
  );

  const renderPeriodStats = () => (
    <Card title="时段统计" style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col span={8}>
          <Statistic
            title="新注册学员"
            value={stats.newRegistrations}
            loading={loading}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="退学学员"
            value={stats.newWithdrawals}
            loading={loading}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="毕业学员"
            value={stats.newGraduates}
            loading={loading}
          />
        </Col>
      </Row>
    </Card>
  );

  const renderMonthlyRegistrations = () => (
    <Card title="月度注册趋势" style={{ marginTop: 16 }}>
      <Table
        dataSource={stats.monthlyRegistrations}
        columns={[
          {
            title: '月份',
            dataIndex: 'month',
            key: 'month'
          },
          {
            title: '注册人数',
            dataIndex: 'count',
            key: 'count'
          }
        ]}
        pagination={false}
      />
    </Card>
  );

  const renderReferrerStats = () => {
    // Filter referrers based on search input and maintain sort order
    const filteredReferrers = stats.referrerStats
      .filter(item => 
        item.name.toLowerCase().includes(referrerSearch.toLowerCase())
      );

    return (
      <Card title="推荐人排名" style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Search
            placeholder="搜索推荐人"
            allowClear
            onChange={e => setReferrerSearch(e.target.value)}
            style={{ width: 300 }}
          />
        </Space>
        <Table
          dataSource={filteredReferrers}
          rowKey="key"
          columns={[
            {
              title: '排名',
              key: 'rank',
              width: 80,
              render: (_, __, index) => index + 1,
              fixed: true
            },
            {
              title: '推荐人',
              dataIndex: 'name',
              key: 'name'
            },
            {
              title: '推荐人数',
              dataIndex: 'count',
              key: 'count',
              sorter: {
                compare: (a, b) => b.count - a.count,
                multiple: 1
              }
            }
          ]}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 位推荐人`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
      </Card>
    );
  };

  return (
    <div>
      {renderOverallStats()}
      <Card>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: 300 }}
          />
        </Space>
        {renderPeriodStats()}
      </Card>
      {renderMonthlyRegistrations()}
      {renderReferrerStats()}
    </div>
  );
};

export default StudentStats; 